import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Loader2, UserCog } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LoginImageCarousel from '@/components/LoginImageCarousel';
import ForgotPasswordModal from '@/components/ForgotPasswordModal';

const UnifiedLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login, beginClientSelection, isLoading, firstAccessUser, setFirstAccessUser } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const redirectByRole = (role: string) => {
    if (role === 'analista') navigate('/tickets');
    else navigate('/dashboard');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (attempts >= 5) {
      toast({
        title: "Muitas tentativas",
        description: "Aguarde alguns minutos antes de tentar novamente.",
        variant: "destructive"
      });
      return;
    }

    try {
      const user = await login(email, password);

      toast({
        title: "Login realizado com sucesso!",
        description: "Bem-vindo ao Portal Átomos."
      });

      redirectByRole(user.role);
    } catch (error: any) {
      // Multi-conta: ativa o modal de escolha sobre o dashboard.
      if (error.needsClientSelection && Array.isArray(error.clients)) {
        beginClientSelection(error.clients, error.preSelectToken);
        navigate('/dashboard');
        return;
      }

      // Verificar se é primeiro acesso
      if (error.firstAccess && error.user) {
        setFirstAccessUser(error.user);
        navigate('/first-access');
        return;
      }

      setAttempts(prev => prev + 1);

      // Tratamento específico para erros de rede
      let errorMessage = error.message || "Email ou senha incorretos.";
      if (error.isNetworkError) {
        errorMessage = error.message || "Não foi possível conectar ao servidor. Verifique se o backend está rodando.";
        console.error('❌ [UnifiedLogin] Erro de rede:', {
          url: error.url,
          originalError: error.originalError
        });
      }

      toast({
        title: "Erro no login",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Formulário de Login - 35% da tela */}
      <div className="w-[35%] flex items-center justify-center bg-white p-8">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-xl overflow-hidden border-2 border-primary-500 shadow-lg">
              <img
                src="/Logo-atomos-preto.png"
                alt="Portal Átomos"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <CardTitle className="text-2xl font-montserrat">Portal Átomos</CardTitle>
              <CardDescription>
                Acesse sua conta para continuar
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  className="border-gray-200 focus:border-primary-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="font-medium">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="border-gray-200 focus:border-primary-500"
                />
              </div>

              {attempts > 0 && (
                <p className="text-sm text-red-600">
                  Tentativas restantes: {5 - attempts}
                </p>
              )}

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="remember"
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <Label htmlFor="remember" className="text-sm text-gray-600">
                      Lembrar de mim
                    </Label>
                  </div>
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-primary-600 hover:text-primary-700 p-0"
                  >
                    Esqueceu a senha?
                  </Button>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-green-600 text-white hover:bg-green-700"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Entrando...
                    </>
                  ) : (
                    'Entrar'
                  )}
                </Button>
              </div>

            </form>
          </CardContent>
        </Card>
      </div>

      {/* Carrossel de Imagens - 65% da tela */}
      <div className="w-[65%]">
        <LoginImageCarousel className="h-screen" />
      </div>

      {/* Modal de Recuperação de Senha */}
      <ForgotPasswordModal
        open={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </div>
  );
};

export default UnifiedLogin;